<script lang="ts">
  import { onMount } from 'svelte';
  import { useLocation, useNavigate } from 'svelte-navigator';
  import { _ } from 'svelte-i18n';

  import { AppState, SetIconsBackground } from '../etc/stores';
  import { cdn, pluralizeI18n } from '../etc/utils';

  import { Container } from '../services/container';
  import { Auth } from '../services/auth';
  import Analytics from '../services/analytics';

  import Page from '../components/misc/Page.svelte';
  import Button from '../components/form/Button.svelte';
  import Link from '../components/misc/Link.svelte';
  import Image from '../components/misc/Image.svelte';
  import Heading from '../components/typography/Heading.svelte';
  import Text from '../components/typography/Text.svelte';
  import { replaceVariables } from '../i18n';
  import {
    AUTH_STORAGE_FLAGS,
    SessionManager
  } from '../services/session-manager';
  import AuthConnector from '../components/misc/AuthConnector.svelte';

  let handleConnectCustomer;

  let clicked_join = false;
  let guest = $AppState.website.flowGuestSignIn;
  let category = 'guest-state';

  const navigate = useNavigate();
  const location = useLocation();
  const auth = Container.get(Auth);

  const session = Container.get(SessionManager) as SessionManager;

  const logo = $AppState.business.logo?.publicPath.large;

  const isCustomerImportState =
    $AppState.customerImportCampaign?.id &&
    $AppState.customerImportCampaign?.status === 'active';

  const coins =
    $location.state?.coins ||
    Number($AppState.customerImportCampaign.coinsReward).toFixed(0);

  const t = (text: string, values: any = {}) => {
    return replaceVariables($_(`customer-import.welcome_page.${text}`), values);
  };

  const onJoin = async () => {
    onGAEventClickJoin();

    if (isCustomerImportState) {
      AppState.update(state => ({
        ...state,
        website: {
          ...state.website,
          authFlow: 'customer-import'
        }
      }));
    }

    if (await auth.isAuthenticatedOnWebsite()) {
      handleConnectCustomer();
    } else if (auth.canOpenLoginModal()) {
      session.set(
        AUTH_STORAGE_FLAGS.customerImport,
        $AppState.customerImportCampaign.id
      );

      auth.openLoginModal({ mode: isLoginFlow ? 'login' : 'signup' });
    }
  };

  const onBeforeConnectCustomer = async () => {
    if (!$AppState.auth?.providerData?.email && $AppState.auth.connected) {
      await auth.loadState();
    }
  };

  const onAfterConnectCustomer = () => {
    onGAEventJoinSuccess();
  };

  $: isLoginFlow = isCustomerImportState || !guest;

  $: category = guest ? 'guest-state' : 'join-state';

  const onGAEventJoinSuccess = () => {
    if (clicked_join) Analytics.logEvent('click-join-success', category);
    clicked_join = false;
  };

  const onGAEventClickJoin = () => {
    if (!clicked_join) Analytics.logEvent('click-join', category);
    clicked_join = true;
  };

  const onGAEventClickHowItWorks = () => {
    Analytics.logEvent('click-how-it-works', category);
  };

  const goToHowItWorks = () => {
    onGAEventClickHowItWorks();
    navigate('/how-it-works');
  };

  const triggerConnectCustomer = () => {
    onGAEventClickJoin();
    handleConnectCustomer();
  };

  onMount(() => {
    Analytics.logPageView(
      'Customer Import Welcome',
      '/customer-import-welcome'
    );

    SetIconsBackground('themed');
  });
</script>

{#if $AppState.business}
  <AuthConnector
    {onAfterConnectCustomer}
    {onBeforeConnectCustomer}
    {triggerConnectCustomer}
    loggerContext="CustomerImportWelcome.svelte"
    let:loading
    bind:handleConnectCustomer
  >
    <div class="twism-welcome">
      <img
        class="twism-welcome__ilustra"
        alt="Coins ilustration"
        src={cdn('/images/welcome-ilustra.png')}
      />
      <Page className="twism-welcome__container" padding="0 24px 16px">
        <div
          class="twism-welcome__coin"
          style={logo
            ? `background-image: url('${process.env.BASE_URL}/images/welcome-coin-background.png');`
            : ''}
        >
          {#if logo}
            <div class="twism-welcome__coin-logo">
              <Image src={logo} round={true} />
            </div>
          {:else}
            <Image src={`${process.env.BASE_URL}/images/welcome-coin.svg`} />
          {/if}
        </div>
        <div class="twism-welcome__title">
          <Heading
            type="h2"
            component="h2"
            textAlign="center"
            class="twism-welcome__title-text"
          >
            <span>{t('title')}</span>
            <span class="twism-welcome__title-coins">
              {t(pluralizeI18n('coins', coins), { COINS: coins })}
            </span>
            <span>{t('sub_title')}</span>
          </Heading>
          <Text
            type="medium"
            color="#90989F"
            textAlign="center"
            class="twism-welcome__title-message"
          >
            {@html t(isLoginFlow ? 'description_login' : 'description_signup')}
          </Text>
        </div>
        {#if $AppState.business.loyaltyEnabled}
          <div
            class="twism-welcome__loyalty"
            style="background: {`${$AppState.theme.colors.illustration.secondary}1a`}"
          >
            <Text
              type="small"
              color="#3D3D3D"
              class="twism-welcome__loyalty-label"
              textAlign="center"
            >
              {t('loyalty', {
                LOYALTY_PERCENTAGE: Number(
                  $AppState.business.rewardsSettings.coinRewardPercentage
                )
              })}
            </Text>
          </div>
        {/if}
        {#if $AppState.auth.providerData || auth.canOpenLoginModal()}
          <div class="twism-welcome__button">
            <Button
              label={t(isLoginFlow ? 'log_in' : 'sign_up')}
              on:click={onJoin}
              {loading}
            />
            <div class="twism-welcome__button-link">
              <Link on:click={goToHowItWorks}>{t('how_it_works')}</Link>
            </div>
          </div>
        {/if}
      </Page>
    </div>
  </AuthConnector>
{/if}

<style lang="scss">
  .twism-welcome {
    background-image: var(--twism-theme-slightGradient);
    background-size: 100% 100%;
    background-repeat: no-repeat;
    height: 100%;
    overflow: hidden;
    &__ilustra {
      width: 100%;
      height: 136px;
    }

    :global(.twism-welcome__container) {
      height: calc(100% - 120px);
      justify-content: center;
      @media screen and (max-height: 800px) {
        height: calc(100% - 80px);
      }

      @media screen and (max-height: 600px) {
        height: calc(100% - 60px);
      }
    }

    &__loyalty {
      margin: 38px 0;
      padding: 4px 8px;
      border-radius: 6px;
    }

    &__coin {
      margin-top: -178px;
      width: 96px;
      height: 96px;
      box-sizing: border-box;
      display: flex;
      justify-content: center;
      align-items: center;
      padding-bottom: 6px;
      padding-left: 2px;
      background-position: center;
      background-repeat: no-repeat;
      background-size: contain;

      &-logo {
        border-radius: 50%;
        overflow: hidden;
        width: 72px;
        height: 72px;
      }
    }

    &__title {
      margin-top: 38px;
      padding: 0 6px;
      outline: none !important;
      font-weight: 600;

      &-coins {
        font-size: 40px;
        font-weight: 700;
        line-height: 28px;
      }

      :global(.twism-welcome__title-text) {
        margin: 0 auto 16px;
        max-width: 276px;
        display: flex;
        flex-direction: column;
      }

      :global(.twism-welcome__title-text > span) {
        display: block;
      }

      :global(.twism-welcome__title-text > span:not(:last-child)) {
        margin-bottom: 16px;
      }
    }

    &__button {
      width: 100%;
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: center;

      &-link {
        margin-top: 24px;
        width: 100%;
      }
    }
  }
</style>
